import styled, { css } from 'styled-components';

import { flexCenterAligned } from '@/styles/common';

import { ButtonSize, ButtonVariant } from './Button.type';

export interface ButtonStyleProps {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $isLoading?: boolean;
  $disabled?: boolean;
  $fullWidth?: boolean;
  $isIcon?: boolean;
}

const brandStyles = css`
  background-color: var(--brand-main);
  color: var(--system-black);

  &:hover {
    background-color: var(--system-yellow);
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--brand-main) 50%, transparent);
  }
`;

const primaryStyles = css`
  background-color: var(--textAndLabels-main);
  color: var(--bg-main);

  &:hover {
    background-color: var(--primary-hover);
  }

  &:disabled {
    background-color: color-mix(
      in srgb,
      var(--textAndLabels-main) 50%,
      transparent
    );
  }
`;

const secondaryStyles = css`
  background-color: var(--bg-secondary);
  color: var(--textAndLabels-main);

  &:hover {
    background-color: var(--system-lightBlue);
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--bg-secondary) 50%, transparent);
  }
`;

const outlinedStyles = css`
  background-color: var(--bg-main);
  border: 1px solid var(--border-main);
  color: var(--textAndLabels-main);
  padding: 9px 19px;

  &:hover {
    background-color: var(--bg-secondary);
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--bg-main) 50%, transparent);
  }
`;

const modalPrimaryStyles = css`
  background-color: var(--system-blue);
  color: var(--promote-button-text);

  &:hover {
    background-color: var(--system-deepBlue);
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--system-blue) 50%, transparent);
  }
`;

const modalSecondaryStyles = css`
  background-color: transparent;
  border: 1px solid var(--delete-button-outline);
  color: var(--delete-button-outline);

  &:hover {
    background-color: var(--delete-button-hover);
  }

  &:disabled {
    background-color: color-mix(
      in srgb,
      var(--delete-button-hover) 50%,
      transparent
    );
  }
`;

const linkStyles = css`
  all: unset;
  cursor: pointer;
  color: var(--system-blue);
  transition: color 0.3s linear;

  @media (pointer: coarse) {
    &:active {
      color: var(--custom-yellow);
      transition: color 0.1s ease;
    }
  }

  @media (pointer: fine) {
    &:hover {
      color: var(--custom-yellow);
    }
  }
`;

const errorStyles = css`
  background-color: #ffeef0;
  color: #ff586d;

  &:hover {
    background-color: #ffdee2;
  }

  &:disabled {
    background-color: color-mix(in srgb, #ffeef0 50%, transparent);
  }
`;

const accentStyles = css`
  background: #75d389;
  color: var(--textAndLabels-main);

  &:hover {
    background-color: #2a9d4c;
  }

  &:disabled {
    background-color: color-mix(in srgb, #75d389 50%, transparent);
  }
`;

const activateStyles = css`
  background-color: var(--system-blue);

  html[data-theme='light'] & {
    color: var(--bg-main);
  }

  html[data-theme='dark'] & {
    color: var(--textAndLabels-main);
  }

  &:hover {
    background-color: var(--system-deepBlue);
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--system-blue) 50%, transparent);
  }
`;

const deactivateStyles = css`
  background-color: var(--bg-main);
  border: 1px solid var(--border-dark);
  color: var(--textAndLabels-main);
  padding: 9px 19px;

  html[data-theme='dark'] & {
    border-color: var(--border-primary);
  }

  &:hover {
    background-color: var(--bg-secondary);
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--bg-main) 50%, transparent);
  }
`;

const removeStyles = css`
  background-color: var(--remove-button-bg);
  border: 1px solid var(--remove-button-bg);
  color: var(--system-red);

  &:hover {
    background-color: var(--remove-button-hover-bg);
  }

  &:disabled {
    background-color: color-mix(
      in srgb,
      var(--remove-button-bg) 50%,
      transparent
    );
  }
`;

const editStyles = css`
  background-color: var(--system-lightBlue);
  border: 1px solid var(--system-lightBlue);
  color: var(--system-blue);

  &:hover {
    background-color: var(--edit-button-bg);
  }

  &:disabled {
    background-color: color-mix(
      in srgb,
      var(--system-lightBlue) 50%,
      transparent
    );
  }
`;

const promoteStyles = css`
  background-color: var(--system-black);
  color: var(--promote-button-text);
  padding-inline: 30px;

  &:hover {
    background-color: #000;
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--system-black) 50%, transparent);
  }
`;

const completeStyles = css`
  background-color: var(--system-green);
  color: var(--textAndLabels-main);

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background-color: color-mix(in srgb, var(--system-green) 50%, transparent);
  }
`;

const largeStyles = css`
  height: 54px;
  font-size: 15px;
`;

const mediumStyles = css`
  height: 44px;
  font-size: 15px;
`;

const smallStyles = css`
  height: 40px;
  font-size: 14px;
`;

const largeIconStyles = css`
  width: 54px;
  min-width: 54px;
  max-width: 54px;
  padding: 0;
`;

const mediumIconStyles = css`
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  padding: 0;
`;

const smallIconStyles = css`
  width: 40px;
  min-width: 40px;
  max-width: 40px;
  padding: 0;
`;

const isLoadingStyles = css`
  background-color: var(--textAndLabels-tertiary);
  color: transparent;
  cursor: wait;

  &::before {
    content: '';
    border: 2px solid #f1f1f1;
    border-top-color: #8d8f97;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    position: absolute;
    animation: spin 1s linear infinite;
  }

  &:hover {
    background-color: var(--textAndLabels-tertiary);
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

function getVariant(variant?: ButtonVariant) {
  switch (variant) {
    case 'brand':
      return brandStyles;
    case 'primary':
      return primaryStyles;
    case 'secondary':
      return secondaryStyles;
    case 'outlined':
      return outlinedStyles;
    case 'modalPrimary':
      return modalPrimaryStyles;
    case 'modalSecondary':
      return modalSecondaryStyles;
    case 'link':
      return linkStyles;
    case 'error':
      return errorStyles;
    case 'accent':
      return accentStyles;
    case 'activate':
      return activateStyles;
    case 'deactivate':
      return deactivateStyles;
    case 'remove':
      return removeStyles;
    case 'edit':
      return editStyles;
    case 'promote':
      return promoteStyles;
    case 'complete':
      return completeStyles;
    default:
      return css``;
  }
}

function getSize(size?: ButtonSize) {
  switch (size) {
    case 's':
      return smallStyles;
    case 'm':
      return mediumStyles;
    case 'l':
      return largeStyles;
    default:
      return css``;
  }
}

function getIconSize(size?: ButtonSize) {
  switch (size) {
    case 's':
      return smallIconStyles;
    case 'm':
      return mediumIconStyles;
    case 'l':
      return largeIconStyles;
    default:
      return css``;
  }
}

export const buttonCss = css<ButtonStyleProps>`
  ${flexCenterAligned};
  border: none;
  border-radius: 28px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 11px 20px;
  gap: 7px;
  position: relative;

  &::before {
    content: none;
  }

  &:disabled {
    background-color: var(--textAndLabels-tertiary);
    cursor: not-allowed;
  }

  ${({ $variant }) => getVariant($variant)}
  ${({ $size }) => getSize($size)}
  ${({ $isIcon, $size }) => ($isIcon ? getIconSize($size) : null)}
  ${({ $isLoading }) => $isLoading && isLoadingStyles}
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

export const ButtonBox = styled.button<ButtonStyleProps>`
  ${buttonCss};
`;
